import DesignButton from 'components/button/DesignButton';
import { CloseIcon, UploadIcon } from 'components/svgs';
import { DragEvent, useRef, useState } from 'react';

export type FileDropzoneProps = {
  /** Value for the file input's `accept` attribute, e.g. `.csv,.xlsx`. */
  accept: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
  /** One 12px line under the drop target, e.g. the size limit. */
  hint?: string;
  /** Id of the file input, so the label can point at it. */
  inputId: string;
};

/** ".csv,.xlsx" -> "CSV or XLSX", so the copy follows the accepted types. */
const acceptedLabel = (accept: string) =>
  accept
    .split(',')
    .map((extension) => extension.trim().replace(/^\./, '').toUpperCase())
    .filter((extension) => extension.length > 0)
    .join(' or ');

const megaBytes = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

/**
 * Drag-and-drop or browse for one file. The file itself is handed to the
 * parent untouched: nothing is read, copied or logged here.
 */
const FileDropzone = ({
  accept,
  file,
  onFileChange,
  disabled,
  hint,
  inputId,
}: FileDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const stop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    stop(event);
    setIsDragOver(false);
    if (disabled) {
      return;
    }
    onFileChange(event.dataTransfer?.files?.[0] ?? null);
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragOver={(event) => {
          stop(event);
          if (!disabled) {
            setIsDragOver(true);
          }
        }}
        onDragLeave={(event) => {
          stop(event);
          setIsDragOver(false);
        }}
        onDrop={handleDrop}
        className={`flex flex-col items-center gap-2 rounded-sm border border-dashed p-8 text-center ${
          isDragOver
            ? 'border-design-red bg-light-gray dark:bg-design-black-accent'
            : 'border-dots-grey'
        } ${disabled ? 'opacity-50' : ''}`}
      >
        <UploadIcon className="h-5 w-5" />
        <span className="text-base">Drop the {acceptedLabel(accept)} here</span>
        <DesignButton
          variant="link"
          disabled={disabled}
          onClickHandler={() => inputRef.current?.click()}
        >
          or browse
        </DesignButton>
        {hint && (
          <span className="text-xs text-light-black dark:text-sublte-white">
            {hint}
          </span>
        )}
        <label htmlFor={inputId} className="sr-only">
          Choose a file
        </label>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          disabled={disabled}
          className="hidden"
          onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
        />
      </div>

      {file && (
        <div className="flex items-center gap-3 rounded-sm border border-design-secondary-black px-3 py-2 text-sm">
          <span className="flex-grow truncate" title={file.name}>
            {file.name}
          </span>
          <span className="shrink-0 text-xs text-light-black dark:text-sublte-white">
            {megaBytes(file.size)} MB
          </span>
          <DesignButton
            variant="ghost"
            size="sm"
            ariaLabel="Remove file"
            title="Remove file"
            disabled={disabled}
            onClickHandler={() => {
              if (inputRef.current) {
                inputRef.current.value = '';
              }
              onFileChange(null);
            }}
          >
            <CloseIcon />
          </DesignButton>
        </div>
      )}
    </div>
  );
};

FileDropzone.defaultProps = {
  disabled: false,
  hint: undefined,
};

export default FileDropzone;
